import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { Tooltip as RechartsTooltip } from 'recharts';
import {
  PhoneAndroid as AndroidIcon,
  PhoneIphone as IosIcon,
  Computer as WebIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { adminApi } from '../services/api';
import { PlatformAnalytics } from '../types';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';

interface PlatformChartProps {
  isVisible?: boolean;
}

const COLORS = {
  ios: '#007AFF',
  android: '#34A853',
  web: '#4285F4',
  unknown: '#9E9E9E',
};

const PlatformChart: React.FC<PlatformChartProps> = ({ isVisible = true }) => {
  const [platformData, setPlatformData] = useState<PlatformAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlatformData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getPlatformAnalytics();
      setPlatformData(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Platform verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Use visibility refresh hook
  const { lastRefresh, manualRefresh } = useVisibilityRefresh(fetchPlatformData, {
    refreshInterval: 80000, // 80 seconds
    isVisible,
    enabled: true,
  });

  useEffect(() => {
    fetchPlatformData();
  }, []);

  const getPlatformIcon = (platform: string) => {
    switch (platform?.toLowerCase()) {
      case 'ios':
        return <IosIcon sx={{ color: COLORS.ios }} />;
      case 'android':
        return <AndroidIcon sx={{ color: COLORS.android }} />;
      case 'web':
        return <WebIcon sx={{ color: COLORS.web }} />;
      default:
        return <WebIcon sx={{ color: COLORS.unknown }} />;
    }
  };

  const formatChartData = () => {
    if (!platformData) return [];

    const chartData = platformData.platformDetails.map(platform => ({
      name: platform._id || 'Bilinmeyen',
      value: platform.totalUsers,
      color: COLORS[platform._id?.toLowerCase() as keyof typeof COLORS] || COLORS.unknown,
    }));

    // Add users without devices if any
    if (platformData.usersWithoutDevices > 0) {
      chartData.push({
        name: 'Cihaz Bilgisi Yok',
        value: platformData.usersWithoutDevices,
        color: COLORS.unknown,
      });
    }

    return chartData;
  };

  const renderCustomLabel = (entry: any) => {
    const percent = ((entry.value / getTotalUsers()) * 100).toFixed(1);
    return `${percent}%`;
  };

  const getTotalUsers = () => {
    if (!platformData) return 0;
    return platformData.platformDetails.reduce((total, platform) => total + platform.totalUsers, 0) + platformData.usersWithoutDevices;
  };

  if (loading) {
    return (
      <Box sx={{ height: 320, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={40} thickness={4} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ height: 320, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const chartData = formatChartData();
  const totalUsers = getTotalUsers();

  return (
    <Box sx={{ height: 420, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Platform Dağılımı
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {format(lastRefresh, 'HH:mm')}
          </Typography>
          <Tooltip title="Yenile">
            <IconButton size="small" onClick={manualRefresh} disabled={loading} sx={{ p: 0.5 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Box sx={{ height: 160, mb: 2, px: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={50}
              innerRadius={20}
              fill="#8884d8"
              dataKey="value"
              paddingAngle={2}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <RechartsTooltip 
              formatter={(value: any) => [`${value}`, 'Kullanıcı']} 
              contentStyle={{
                borderRadius: 8,
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ maxHeight: 160, overflow: 'auto', px: 0.5 }}>
        {chartData.map((platform, index) => {
          const percentage = ((platform.value / totalUsers) * 100).toFixed(1);
          return (
            <Box 
              key={index} 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center" 
              sx={{ 
                py: 1,
                px: 1,
                minHeight: 40,
                borderRadius: 1,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                mb: 0.5
              }}
            >
              <Box 
                display="flex" 
                alignItems="center" 
                gap={1.5}
                sx={{ 
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden'
                }}
              >
                {getPlatformIcon(platform.name)}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  {platform.name}
                </Typography>
              </Box>
              <Box 
                sx={{ 
                  flexShrink: 0,
                  ml: 1,
                  minWidth: 'fit-content'
                }}
              >
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {platform.value} ({percentage}%)
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box mt={1} p={1} bgcolor="grey.50" borderRadius={1}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Toplam: {totalUsers.toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default PlatformChart;
